import {
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Theme,
  useTheme,
} from "@material-ui/core";
import { Minimize, Visibility } from "@material-ui/icons";
import Close from "@material-ui/icons/Close";
import { AxiosPromise } from "axios";
import { useEffect } from "react";
import { useContext, useState } from "react";
import { Button } from "react-admin";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import assets from "..";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import resourceNames from "../../../common/data/resourceNames";
import {
  DvirFormAreaPointSeverity,
  Int32CommonDto,
  MaintenanceDashboardApiFactory,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { CustomResourceRouteParams } from "../../../common/types";
import { SelectedVehiclesFilterContext, useButtonStyles } from "./common";

interface Props {
  handleSidebar: (isVisible: boolean) => void;
}

export const SelectedVehiclesList = (props: Props) => {
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(true);
  const filterContext = useContext(SelectedVehiclesFilterContext);
  const [data, setData] = useState<Int32CommonDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const axios = useAxiosInstance();
  const buttonClasses = useButtonStyles();
  const history = useHistory();
  const params = useParams<CustomResourceRouteParams>();
  const vehicleId = params.id;
  const theme = useTheme<Theme>();
  const { handleSidebar } = props;

  useEffect(() => {
    handleSidebar(sidebarVisible);
  }, [sidebarVisible, handleSidebar]);

  useEffect(() => {
    if (!axios) {
      return;
    }

    if (!filterContext) {
      return;
    }

    if (!filterContext.selectedFilter) {
      return;
    }

    let func:
      | ((options?: any) => AxiosPromise<Int32CommonDto[]>)
      | undefined = undefined;
    const factory = MaintenanceDashboardApiFactory(
      undefined,
      axios.defaults.baseURL,
      axios
    );
    switch (filterContext.selectedFilter) {
      case "WITH_DIAGNOSTIC_ERRORS":
        func =
          factory.apiAppMaintenancedashboardVehicleswithdiagnosticerrorsGet;
        break;
      case "WITH_ELD_ALERTS":
        func = factory.apiAppMaintenancedashboardVehicleswithgeometrisalertsGet;
        break;
      case "WITH_TPMS_ALERTS":
        func = factory.apiAppMaintenancedashboardVehicleswithtpmsalertsGet;
        break;
      case "WITH_UNRESOLVED_DVIR_DEFECTS":
        func = () =>
          factory.apiAppMaintenancedashboardVehicleswithunresolveddvirdefectsGet(
            DvirFormAreaPointSeverity.None
          );
        break;
      case "WITH_MAJOR_UNRESOLVED_DVIR_DEFECTS":
        func = () =>
          factory.apiAppMaintenancedashboardVehicleswithunresolveddvirdefectsGet(
            DvirFormAreaPointSeverity.Major
          );
        break;
      case "WITH_MINOR_UNRESOLVED_DVIR_DEFECTS":
        func = () =>
          factory.apiAppMaintenancedashboardVehicleswithunresolveddvirdefectsGet(
            DvirFormAreaPointSeverity.Minor
          );
        break;
      case "WITH_OVERDUE_SERVICES":
        func = factory.apiAppMaintenancedashboardVehicleswithoverdueservicesGet;
        break;
      case "WITH_UPCOMING_SERVICES":
        func =
          factory.apiAppMaintenancedashboardVehicleswithupcomingservicesGet;
        break;
    }

    if (!func) {
      return;
    }

    setLoading(true);
    func()
      .then((response) => {
        setData(response.data);
      })
      .catch((e) => console.error(handleAxiosError(e)))
      .finally(() => setLoading(false));
  }, [axios, filterContext]);

  return loading ? (
    <CircularProgress size={12} />
  ) : (
    <div className="selected-vehicles-list-wrapper">
      <div
        className="title-bar"
        style={{ background: theme.palette.background.paper }}>
        <IconButton onClick={() => setSidebarVisible((x) => !x)}>
          {sidebarVisible ? <Minimize /> : <Visibility />}
        </IconButton>

        <IconButton
          onClick={() =>
            filterContext && filterContext.setSelectedFilter(undefined)
          }>
          <Close />
        </IconButton>
        <Divider />
      </div>
      {sidebarVisible ? (
        <div
          className="selected-vehicles-list"
          style={{ background: theme.palette.background.paper }}>
          <List dense>
            {data.map((x) => (
              <ListItem key={x.id}>
                <Button
                  label={x.name!}
                  className={buttonClasses.label}
                  style={
                    vehicleId === `${x.id}`
                      ? { fontWeight: 700, color: theme.palette.info.light }
                      : {}
                  }
                  onClick={() =>
                    history.push(
                      `/${resourceNames.ASSETS_MAINTENANCE_DASHBOARD}/${x.id}`
                    )
                  }>
                  <assets.icon />
                </Button>
              </ListItem>
            ))}
          </List>
        </div>
      ) : null}
      <div
        className={`${
          sidebarVisible
            ? "selected-vehicles-list-footer"
            : "selected-vehicles-list-footer-closed"
        }`}
        style={{ background: theme.palette.background.paper }}>
        &nbsp;
      </div>
    </div>
  );
};
